/**
 * A document that is a file rather than text (`DocumentDoc.bytes`) and that
 * no app draws: an image, or something the window cannot show. A PDF opens in
 * the PDF reader app once one is installed; until then it lands here.
 *
 * The URL is made once per `sourceKey`, not per render: `DocumentView`
 * re-renders on every presence beat, and a file document's bytes never
 * change, so its id and size name it.
 */

import { useEffect, useMemo, useRef } from 'react'

export function FileView({
  bytes,
  mime,
  title,
  sourceKey,
}: {
  bytes: Uint8Array
  mime: string
  title: string
  /** Names these bytes: the same key means the same file. */
  sourceKey: string
}) {
  const held = useRef<{ key: string; bytes: Uint8Array } | null>(null)
  if (held.current?.key !== sourceKey) held.current = { key: sourceKey, bytes }
  const stable = held.current.bytes

  const url = useMemo(
    () =>
      URL.createObjectURL(
        new Blob([new Uint8Array(stable)], {
          type: mime || 'application/octet-stream',
        }),
      ),
    [stable, mime],
  )
  useEffect(() => () => URL.revokeObjectURL(url), [url])

  if (mime.startsWith('image/')) {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <img src={url} alt={title} className="max-w-full" />
      </div>
    )
  }
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-2 text-muted-foreground text-sm">
      <div>
        {title}: {mime || 'a file'}, {formatBytes(stable.byteLength)}
      </div>
      {mime === 'application/pdf' ? (
        <div>Install the PDF reader from Packages to read it here.</div>
      ) : null}
      <a href={url} download={title} className="underline">
        Download
      </a>
    </div>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} bytes`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}
